import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

const AI_PROVIDERS = ['OpenAI', 'Claude', 'Gemini', 'Azure OpenAI', 'Cohere', 'Mistral'];

function AIKeyModal({ tenantId, config, onClose, onSaved }) {
  const isEdit = !!config;
  const [form, setForm] = useState({
    provider: config?.Provider || '',
    apiKey: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.provider || (!isEdit && !form.apiKey)) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/settings/ai-keys/${config.AIApiKeyID}`, form);
        toast.success('AI API key updated');
      } else {
        await api.post('/settings/ai-keys', { tenantId, ...form });
        toast.success('AI API key added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit AI API Key' : 'Add AI API Key'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">AI Provider</label>
            <select value={form.provider} onChange={(e) => setForm((p) => ({ ...p, provider: e.target.value }))} className="form-select">
              <option value="">Select a provider</option>
              {AI_PROVIDERS.map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
          </div>
          <div className="form-group">
            <label className="required-label">API Key</label>
            <input type="password" placeholder={isEdit ? 'Leave blank to keep existing' : 'Enter API key'} value={form.apiKey} onChange={(e) => setForm((p) => ({ ...p, apiKey: e.target.value }))} />
            {!form.provider && <span className="form-hint">Select a provider first</span>}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              <span>💾</span> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AIKeysConfig({ tenantId }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editKey, setEditKey] = useState(null);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/ai-keys/${tenantId}`);
      if (response.data.success) setKeys(response.data.data);
    } catch {
      toast.error('Failed to load AI API keys');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleDelete = async (id) => {
    if (!(await confirmDelete('API Key'))) return;
    try {
      await api.delete(`/settings/ai-keys/${id}`);
      toast.success('API key deleted');
      loadKeys();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'numeric', year: 'numeric' }) : '-';

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>AI API Key Configuration</h3>
        <button className="btn btn-add" onClick={() => { setEditKey(null); setShowModal(true); }}>
          + Add AI Key
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : keys.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No AI API keys configured. Click "Add AI Key" to add one.</div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Provider</th>
              <th>API Key</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.AIApiKeyID}>
                <td><span className="badge badge-provider">{k.Provider}</span></td>
                <td><code style={{ fontSize: 13 }}>{k.ApiKey}</code></td>
                <td>
                  <span className={`badge ${k.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                    {k.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{formatDate(k.CreatedAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditKey(k); setShowModal(true); }}>✏</button>
                    <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(k.AIApiKeyID)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <AIKeyModal
          tenantId={tenantId}
          config={editKey}
          onClose={() => { setShowModal(false); setEditKey(null); }}
          onSaved={() => { setShowModal(false); setEditKey(null); loadKeys(); }}
        />
      )}
    </>
  );
}

export default AIKeysConfig;
